import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { BehaviorSubject, throwError, timeout } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

const REQUEST_TIMEOUT_MS = 30_000;
const PUBLIC_ROUTES = ['/assets/'];

/**
 * Set via `new HttpContext().set(SKIP_ERROR_TOAST, true)` on a request whose
 * error responses are expected/benign (e.g. a 404 used purely as an
 * existence check) and shouldn't surface the interceptor's generic error
 * toast. The error itself still propagates to the caller's own `subscribe`.
 */
export const SKIP_ERROR_TOAST = new HttpContextToken(() => false);

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Latched once a session-expiry logout has started. Everything in flight when the session dies
 * comes back 401 at roughly the same moment, and each response used to run the whole logout path —
 * which is how one expiry produced a stack of identical "session has expired" toasts. Never reset:
 * the logout ends in a full page navigation, so the flag dies with the document.
 */
let isLoggingOut = false;

/**
 * Functional HTTP interceptor — Angular 20 pattern.
 * Attaches Bearer token, single-flight refresh on 401, 30s timeout, and
 * surfaces error toasts. Final 401 redirects to Company SSO.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (PUBLIC_ROUTES.some(route => req.url.includes(route))) {
    return next(req);
  }

  const authService = inject(AuthService);
  const toastService = inject(ToastService);

  const token = authService.getAccessToken();
  if (token) {
    req = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  return next(req).pipe(
    timeout(REQUEST_TIMEOUT_MS),
    catchError((error: HttpErrorResponse | Error) => {
      if (error.name === 'TimeoutError') {
        toastService.error('Request timed out. Please try again.');
        return throwError(() => error);
      }

      if (error instanceof HttpErrorResponse) {
        if (error.status === 401) {
          return handle401(req, next, authService, toastService);
        }

        // Benign/expected errors (e.g. a 404 used as an existence check)
        // still propagate to the caller — only the generic toast is skipped.
        if (req.context.get(SKIP_ERROR_TOAST)) {
          return throwError(() => error);
        }

        switch (error.status) {
          case 400:
          case 422: {
            const { title, detail } = extractClientErrorMessage(error);
            toastService.error(title, detail);
            break;
          }

          case 403: {
            const msg = error.error?.detail || 'Access denied. You do not have permission to perform this action.';
            toastService.error('Forbidden', msg);
            break;
          }

          case 404: {
            const msg = error.error?.detail || 'The requested resource was not found.';
            toastService.error('Not Found', msg);
            break;
          }

          case 409: {
            const msg = error.error?.detail || 'A conflict occurred. Please refresh and try again.';
            toastService.error('Conflict', msg);
            break;
          }

          case 429:
            toastService.error('Too Many Requests', 'Too many requests. Please try again later.');
            break;

          case 502:
          case 503:
            toastService.error('Service Unavailable', 'Service temporarily unavailable. Please try again later.');
            break;

          case 504:
            toastService.error('Timeout', 'Request timed out. Please try again.');
            break;

          default:
            if (error.status >= 400 && error.status < 500) {
              const { title, detail } = extractClientErrorMessage(error);
              toastService.error(title, detail);
            } else if (error.status >= 500) {
              toastService.error('A server error occurred. Please try again later.');
            }
            break;
        }
      }

      return throwError(() => error);
    })
  );
};

function handle401(
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  authService: AuthService,
  toastService: ToastService
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = authService.getRefreshToken();

    if (refreshToken && !refreshToken.startsWith('refresh-')) {
      return authService.refreshAccessToken(refreshToken).pipe(
        switchMap(response => {
          isRefreshing = false;
          authService.setAccessToken(response.accessToken);
          refreshTokenSubject.next(response.accessToken);
          return next(req.clone({ setHeaders: { Authorization: `Bearer ${response.accessToken}` } }));
        }),
        catchError(err => {
          isRefreshing = false;
          doRedirectToLogin(authService, toastService);
          return throwError(() => err);
        })
      );
    } else {
      isRefreshing = false;
      doRedirectToLogin(authService, toastService);
      return throwError(() => new Error('Session expired'));
    }
  } else {
    return refreshTokenSubject.pipe(
      filter((t): t is string => t !== null),
      take(1),
      switchMap(t => next(req.clone({ setHeaders: { Authorization: `Bearer ${t}` } })))
    );
  }
}

function doRedirectToLogin(authService: AuthService, toastService: ToastService): void {
  if (isLoggingOut) return;
  isLoggingOut = true;

  toastService.error('Your session has expired. Please log in again.');
  authService.clearAuthData();
  authService.redirectToLogin();
}

/**
 * Extract a user-facing message from a client error (4xx) response.
 * The API is expected to return meaningful messages for client errors
 * (validation failures, not found, forbidden, etc.).
 */
function extractClientErrorMessage(error: HttpErrorResponse): { title: string; detail: string } {
  const body = error.error;

  // Validation errors array: [{ field, message }]
  if (Array.isArray(body?.errors) && body.errors.length > 0) {
    const messages = body.errors.map((e: { message?: string }) => e.message).filter(Boolean);
    if (messages.length > 0) {
      return { title: body.title || 'Validation Error', detail: messages.join(' ') };
    }
  }

  // RFC 7807 detail field
  if (typeof body?.detail === 'string' && body.detail.trim()) {
    return { title: body.title || 'Error', detail: body.detail };
  }

  // Standard message field
  if (typeof body?.message === 'string' && body.message.trim()) {
    return { title: body.title || 'Error', detail: body.message };
  }

  // At least a title from the API
  if (typeof body?.title === 'string' && body.title.trim()) {
    return { title: body.title, detail: '' };
  }

  // Plain string body
  if (typeof body === 'string' && body.trim()) {
    return { title: 'Error', detail: body };
  }

  // Nothing usable in the body — single generic fallback
  return { title: 'Request Failed', detail: `The server returned an error (${error.status}). Please try again.` };
}
